import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, MapPin, Phone } from "lucide-react";

const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_naija-eats-6/artifacts/gjad5l4b_ChopBoxCo%20Green%20Logo%20PNG.png";
const TURKEY = "/turkey-hero.png";
const CHICKEN = "/chicken-hero.png";
const YAM = "/yam-plantain-hero.png";
const PUFFPUFF = "/puff-puff-hero.png";

const marqueeItems = ["Meat Pies", "Puff Puff", "Fried Plantain", "Turkey Wings", "Fried Chicken", "Fried Beef", "Jollof Rice", "Family Platter"];

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO — Bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-14 pb-16 md:pb-24">
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="col-span-12 lg:col-span-7 chop-card chop-shadow p-8 md:p-12 relative overflow-hidden"
          >
            <div className="font-label text-[11px] text-[#D02030] mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#D02030]" />
              Freshly made · Small chops, big flavour
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tighter text-[#0E2B13]">
              Small chops.<br />
              <span className="font-script text-[#D02030] text-5xl md:text-7xl">Big flavour.</span>
            </h1>
            <p className="mt-6 text-lg text-[#4E5C4F] max-w-xl">
              Meat pies, puff puff, samosas, turkey wings & platters — cooked fresh in Hamilton and Brantford, delivered hot to your door or ready for free pickup.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/menu"
                data-testid="hero-order-btn"
                className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-4 text-base font-semibold"
              >
                Order the menu
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#story" className="rounded-full px-6 py-4 text-sm font-medium text-[#0E2B13] border border-[#1A6B2E]/20 hover:bg-[#F4EFEB]">
                Read our story
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-[#4E5C4F]">
              <div className="flex items-center gap-2"><Flame className="w-4 h-4 text-[#D02030]" /> Freshly made</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#1A6B2E]" /> Hamilton · Brantford</div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#1A6B2E]" /> +1 (226) 552-5375</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4 md:gap-6"
          >
            <div className="col-span-2 chop-card chop-shadow overflow-hidden aspect-[4/3] lg:aspect-auto lg:h-64">
              <img src={TURKEY} alt="Fried turkey wings" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 chop-card chop-shadow overflow-hidden aspect-[16/7]">
              <img src={CHICKEN} alt="Fried chicken" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#1A6B2E]/10 bg-[#1A6B2E] text-[#FDFBF7] py-5 overflow-hidden">
        <div className="marquee font-display text-3xl md:text-5xl tracking-tighter">
          {[...marqueeItems, ...marqueeItems].map((t, i) => (
            <span key={i} className="flex items-center gap-10 whitespace-nowrap">
              <span>{t}</span>
              <span className="text-[#E29A26]">✳</span>
            </span>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-label text-xs text-[#D02030]">Our story</span>
            <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tighter">
              Home kitchen energy,<br />
              <span className="font-script text-[#D02030]">boxed with love.</span>
            </h2>
            <p className="mt-6 text-lg text-[#4E5C4F] max-w-lg">
              Chop Box Co. is a small kitchen serving Hamilton, Brantford and nearby with authentic Nigerian & West-African small chops — the flaky meat pies, cloud-soft puff puff and golden turkey wings you'd find at the best family parties back home.
            </p>
            <p className="mt-4 text-lg text-[#4E5C4F] max-w-lg">
              Freshly made. Great taste. Made with care. Made for you.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-[#1A6B2E]">
              <img src={LOGO} alt="" className="w-10 h-10" />
              <span className="font-script text-2xl text-[#D02030]">Small chops. Big flavour.</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="chop-card chop-shadow overflow-hidden aspect-[3/4]">
              <img src={YAM} alt="Fried yam and plantain" className="w-full h-full object-cover" />
            </div>
            <div className="chop-card chop-shadow overflow-hidden aspect-[3/4]">
              <img src={PUFFPUFF} alt="Puff puff" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section id="contact" className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="chop-card chop-shadow p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-3xl md:text-5xl tracking-tighter text-[#0E2B13]">Ready to order?</h3>
            <p className="mt-2 text-[#0E2B13]/80 max-w-lg">
              Pick your platter, choose pickup or home delivery — pre-orders recommended.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#0E2B13]/75">
              <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> +1 (226) 552-5375</span>
              <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> Hamilton · Brantford</span>
            </div>
          </div>
          <Link
            to="/menu"
            data-testid="cta-order-btn"
            className="btn-red rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center gap-2"
          >
            See the menu <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
